import React, { useCallback, useMemo, useState } from 'react';
import fileSaver from 'file-saver';
import { toast } from 'sonner';
// `file-saver`'s module interop differs between the browser (Vite/esbuild) and
// tsx/Node ESM: the default export can be the `saveAs` FUNCTION or a namespace
// object `{ saveAs }`. A bare `import saveAs from 'file-saver'` then yields the
// namespace object in the browser bundle, so `saveAs(blob, name)` throws
// "saveAs is not a function" — which (with no catch) silently broke every
// roadmap export. Normalize so the call works under either interop.
const saveAs: (data: Blob, filename?: string) => void =
  typeof fileSaver === 'function'
    ? (fileSaver as unknown as (data: Blob, filename?: string) => void)
    : (fileSaver as unknown as { saveAs: (data: Blob, filename?: string) => void }).saveAs;
import { Download, FileText, Loader2, Presentation, Printer, Plus, RefreshCw, X } from 'lucide-react';
import type { SiteEntity } from '../../types/site';
import type {
  RecommendationGraph,
  RecommendationNode,
  RecommendationPhase,
  RecommendationStatus,
} from '../../types/recommendationGraph';
import { PHASE_ORDER } from '../../lib/audit/recommendationGraphBuilder';
import { roadmapSourceReportDelta } from '../../lib/audit/roadmapSourceReports';
import {
  normalizeRecommendationStatus,
  RECOMMENDATION_STATUS_ORDER,
  recommendationStatusBadgeClass,
  recommendationStatusLabel,
} from '../../lib/audit/recommendationStatus';
import { useStore } from '../../store';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { relativeTime } from '../ui/SaveIndicator';
import {
  buildRoadmapDocxBlob,
  buildRoadmapPptx,
  printRoadmap,
  roadmapDocxFilename,
  roadmapPptxFilename,
} from '../../lib/export/roadmapExport';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';

// R-131-ROADMAP-FIX-A — 6th time-bucket tab for items without a deadline
const PHASE_LABELS: Record<RecommendationPhase, string> = {
  immediate: 'Immediate',
  '30d': '30 days',
  '90d': '90 days',
  '6mo': '6 months',
  '12mo+': '12mo+',
  unscheduled: 'Unscheduled',
};

/** Order saved actions by their recorded deadline; no schedule is inferred. */
function sortByDeadline(nodes: RecommendationNode[]): RecommendationNode[] {
  return [...nodes].sort((a, b) => {
    const left = a.deadline ? Date.parse(a.deadline) : NaN;
    const right = b.deadline ? Date.parse(b.deadline) : NaN;
    if (Number.isFinite(left) && Number.isFinite(right)) return left - right;
    if (Number.isFinite(left)) return -1;
    if (Number.isFinite(right)) return 1;
    return 0;
  });
}

function statusBreakdown(nodes: RecommendationNode[]): Record<RecommendationStatus, number> {
  const counts: Record<RecommendationStatus, number> = {
    'not-started': 0, initiated: 0, 'pending-feedback': 0, blocked: 0, completed: 0,
  };
  for (const node of nodes) counts[normalizeRecommendationStatus(node.status)] += 1;
  return counts;
}

function Drawer({ node, graph, siteId, onClose }: {
  node: RecommendationNode;
  graph: RecommendationGraph;
  siteId: string;
  onClose: () => void;
}) {
  const updateStatus = useStore(s => s.updateRecommendationStatus);
  const prerequisites = graph.edges.filter(edge => edge.target === node.id);
  const downstream = graph.edges.filter(edge => edge.source === node.id);
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent size="lg" showCloseButton={false} data-testid="roadmap-drawer">
      <div className="flex items-center justify-between gap-3">
        <DialogTitle>Action details</DialogTitle>
        <Button type="button" variant="ghost" className="min-h-11 min-w-11" onClick={onClose} aria-label="Close drawer"><X className="h-4 w-4" aria-hidden /></Button>
      </div>
      <DialogDescription className="text-sm text-text-secondary">Review the recommendation and update its recorded progress.</DialogDescription>
      <p className="whitespace-pre-wrap break-words text-base leading-relaxed text-text-primary">{node.canonicalForm}</p>
      <dl className="grid gap-2 text-sm sm:grid-cols-2">
        <div><dt className="text-text-muted">Owner</dt><dd>{node.owner || 'Not assigned'}</dd></div>
        <div><dt className="text-text-muted">Timing</dt><dd>{PHASE_LABELS[node.phase]}{node.deadline ? ` · ${node.deadline}` : ''}</dd></div>
      </dl>
      <label className="block text-sm font-medium">Status
        <select className="mt-1 min-h-11 w-full rounded-md border border-border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          value={normalizeRecommendationStatus(node.status)} data-testid="roadmap-status-select"
          onChange={event => void updateStatus(siteId, node.id, event.target.value as RecommendationStatus)}>
          {RECOMMENDATION_STATUS_ORDER.map(status => <option key={status} value={status}>{recommendationStatusLabel(status)}</option>)}
        </select>
      </label>
      <details className="border-t border-border-subtle pt-2" data-testid="roadmap-node-sources">
        <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Source reports ({node.sourceReports.length})</summary>
        <ul className="space-y-2 pb-2 text-sm text-text-secondary">
          {node.sourceReports.map((source, index) => <li key={`${source.reportId}-${source.sectionId}-${index}`}>
            <p>{source.reportType} · {source.sectionId}</p><p className="break-all text-xs text-text-muted">Report {source.reportId}</p>
          </li>)}
        </ul>
      </details>
      {prerequisites.length > 0 && <details className="border-t border-border-subtle pt-2" data-testid="roadmap-node-prerequisites">
        <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Prerequisites ({prerequisites.length})</summary>
        <ul className="space-y-2 text-sm text-text-secondary">{prerequisites.map(edge => <li key={edge.id}>
          {graph.nodes.find(item => item.id === edge.source)?.canonicalForm ?? edge.source}
          <p className="text-xs text-text-muted">{edge.reason ?? edge.type}</p>
        </li>)}</ul>
      </details>}
      {downstream.length > 0 && <details className="border-t border-border-subtle pt-2" data-testid="roadmap-node-downstream">
        <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Dependent actions ({downstream.length})</summary>
        <ul className="space-y-2 text-sm text-text-secondary">{downstream.map(edge => <li key={edge.id}>
          {graph.nodes.find(item => item.id === edge.target)?.canonicalForm ?? edge.target}
          <p className="text-xs text-text-muted">{edge.reason ?? edge.type}</p>
        </li>)}</ul>
      </details>}
    </DialogContent>
  </Dialog>;
}

function RecommendationGraphFlow({ graph, siteId }: { graph: RecommendationGraph; siteId: string }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [phaseFilter, setPhaseFilter] = useState<RecommendationPhase | null>(null);
  const updateStatus = useStore(s => s.updateRecommendationStatus);
  const byPhase = useMemo(() => {
    const groups = new Map<RecommendationPhase, RecommendationNode[]>();
    for (const phase of PHASE_ORDER) groups.set(phase, []);
    for (const node of graph.nodes) (groups.get(node.phase) ?? groups.get('unscheduled')!).push(node);
    for (const phase of PHASE_ORDER) groups.set(phase, sortByDeadline(groups.get(phase) ?? []));
    return groups;
  }, [graph.nodes]);
  const breakdown = useMemo(() => statusBreakdown(graph.nodes), [graph.nodes]);
  // A reading shortcut, not a claim that dependencies are cleared or work is approved.
  const nextToReview = PHASE_ORDER.flatMap(phase => byPhase.get(phase) ?? [])
    .find(node => normalizeRecommendationStatus(node.status) !== 'completed');
  const selected = selectedId ? graph.nodes.find(node => node.id === selectedId) : null;
  const visiblePhases = PHASE_ORDER.filter(phase => (phaseFilter ? phase === phaseFilter : (byPhase.get(phase)?.length ?? 0) > 0));
  return <div className="space-y-5">
    <section className="space-y-3 border-b border-border-subtle pb-4" data-testid="roadmap-progress-overview">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-text-primary">{nextToReview ? 'Next to review' : 'All actions marked completed'}</h2>
        <p className="text-sm text-text-muted">{breakdown.completed} of {graph.nodes.length} actions marked completed</p>
      </div>
      {nextToReview ? <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="roadmap-next-action">
        <div className="min-w-0 max-w-prose">
          <p className="line-clamp-3 text-base leading-relaxed text-text-primary">{nextToReview.canonicalForm}</p>
          <p className="mt-1 text-sm text-text-muted">{recommendationStatusLabel(normalizeRecommendationStatus(nextToReview.status))} · {PHASE_LABELS[nextToReview.phase]}{nextToReview.owner ? ` · ${nextToReview.owner}` : ''}</p>
        </div>
        <Button type="button" variant="outline" className="min-h-11 shrink-0" onClick={() => setSelectedId(nextToReview.id)}>Review action</Button>
      </div> : <p className="max-w-prose text-sm text-text-muted">Statuses reflect recorded roadmap progress. Source and decision qualifications remain attached to the reports.</p>}
      <details data-testid="roadmap-status-details">
        <summary className="min-h-11 cursor-pointer py-3 text-sm text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Status breakdown</summary>
        <dl className="flex flex-wrap gap-x-6 gap-y-2 pb-2 text-sm">{RECOMMENDATION_STATUS_ORDER.map(status => <div key={status} className="flex gap-2"><dt>{recommendationStatusLabel(status)}</dt><dd className="font-semibold tabular-nums">{breakdown[status]}</dd></div>)}</dl>
      </details>
    </section>
    <div className="flex flex-wrap gap-2" data-testid="roadmap-phase-legend" role="group" aria-label="Filter roadmap by deadline bucket">
      <button type="button" className="min-h-11 rounded-md border border-border px-3 text-sm aria-pressed:border-primary aria-pressed:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-pressed={phaseFilter === null} data-testid="roadmap-phase-tab-all" onClick={() => setPhaseFilter(null)}>All ({graph.nodes.length})</button>
      {PHASE_ORDER.map(phase => {
        const count = byPhase.get(phase)?.length ?? 0;
        return <button type="button" key={phase} disabled={count === 0} aria-pressed={phaseFilter === phase}
          className="min-h-11 rounded-md border border-border px-3 text-sm aria-pressed:border-primary aria-pressed:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
          data-testid={`roadmap-phase-tab-${phase}`} onClick={() => setPhaseFilter(previous => previous === phase ? null : phase)}>
          {PHASE_LABELS[phase]} ({count})
        </button>;
      })}
    </div>
    <section aria-label="Roadmap actions" className="space-y-5" data-testid="roadmap-stage">
      {visiblePhases.map(phase => <section key={phase} aria-labelledby={`roadmap-group-${phase}`} data-testid={`roadmap-col-${phase}`}>
        <div className="flex items-baseline justify-between gap-2 border-b border-border-subtle pb-2">
          <h3 id={`roadmap-group-${phase}`} className="text-sm font-semibold text-text-primary">{PHASE_LABELS[phase]}</h3>
          <span className="text-sm text-text-muted">{byPhase.get(phase)?.length ?? 0} actions</span>
        </div>
        <ul className="divide-y divide-border-subtle">{(byPhase.get(phase) ?? []).map(node => {
          const prerequisiteCount = graph.edges.filter(edge => edge.target === node.id).length;
          return <li key={node.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <button type="button" data-testid={`roadmap-node-${node.id}`} aria-label={`Open recommendation: ${node.canonicalForm}`}
              onClick={() => setSelectedId(node.id)} className="min-h-11 min-w-0 flex-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <span className="block max-w-prose whitespace-pre-wrap break-words text-sm font-medium leading-relaxed text-text-primary">{node.canonicalForm}</span>
              <span className="mt-1 block text-xs text-text-muted">{node.owner || 'Owner not assigned'}{node.deadline ? ` · ${node.deadline}` : ''}{prerequisiteCount > 0 ? ` · ${prerequisiteCount} prerequisite${prerequisiteCount === 1 ? '' : 's'}` : ''} · {node.sourceReports.length} source{node.sourceReports.length === 1 ? '' : 's'}</span>
            </button>
            <select value={normalizeRecommendationStatus(node.status)} aria-label={`Status: ${node.canonicalForm}`}
              data-testid={`roadmap-status-${node.id}`} className="min-h-11 w-full rounded-md border border-border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:w-44 sm:shrink-0"
              onChange={event => void updateStatus(siteId, node.id, event.target.value as RecommendationStatus)}>
              {RECOMMENDATION_STATUS_ORDER.map(status => <option key={status} value={status}>{recommendationStatusLabel(status)}</option>)}
            </select>
          </li>;
        })}</ul>
      </section>)}
    </section>
    {selected && <Drawer node={selected} graph={graph} siteId={siteId} onClose={() => setSelectedId(null)} />}
  </div>;
}

interface Props {
  site: SiteEntity;
  compact?: boolean;
  graphOverride?: RecommendationGraph;
}

export function RecommendationGraphView({ site, compact = false, graphOverride }: Props) {
  const entry = useStore((s) => s.recommendationGraphBySite[site.id]);
  const rebuild = useStore((s) => s.rebuildRecommendationGraph);
  const incrementalUpdate = useStore((s) => s.incrementalUpdateRecommendationGraph);
  const reports = useStore((s) => s.siteRag.reports);
  const graph = graphOverride ?? entry?.graph;
  const building = entry?.building ?? false;
  const [exporting, setExporting] = useState(false);
  const allUnscheduled = Boolean(
    graph && graph.nodes.length > 0 && graph.nodes.every((n) => n.phase === 'unscheduled'),
  );

  // Replacements and removals also need an update to retract obsolete advice.
  const sourceChangeCount = useMemo(() => {
    if (!graph) return 0;
    const delta = roadmapSourceReportDelta(graph.reportIds, reports.filter((r) => r.siteId === site.id));
    return delta.newReports.length + delta.retiredReportIds.length;
  }, [graph, reports, site.id]);

  const onExportWord = useCallback(async () => {
    if (!graph) return;
    setExporting(true);
    try {
      const blob = await buildRoadmapDocxBlob(graph, { id: site.id, name: site.name });
      const isoDay = new Date().toISOString().slice(0, 10);
      saveAs(blob, roadmapDocxFilename({ name: site.name }, isoDay));
    } catch (err) {
      console.error('[roadmap] Word export failed', err);
      toast.error('Word export failed', {
        description: err instanceof Error ? err.message : 'Unexpected error generating the .docx file.',
      });
    } finally {
      setExporting(false);
    }
  }, [graph, site.id, site.name]);

  const onExportPptx = useCallback(async () => {
    if (!graph) return;
    setExporting(true);
    try {
      const blob = await buildRoadmapPptx(graph, { id: site.id, name: site.name });
      const isoDay = new Date().toISOString().slice(0, 10);
      saveAs(blob, roadmapPptxFilename({ name: site.name }, isoDay));
    } catch (err) {
      console.error('[roadmap] PowerPoint export failed', err);
      toast.error('PowerPoint export failed', {
        description: err instanceof Error ? err.message : 'Unexpected error generating the .pptx file.',
      });
    } finally {
      setExporting(false);
    }
  }, [graph, site.id, site.name]);

  const onPrint = useCallback(() => {
    if (!graph) return;
    try {
      printRoadmap(graph, { name: site.name });
    } catch (err) {
      console.error('[roadmap] Print failed', err);
      toast.error('Print failed', {
        description: err instanceof Error ? err.message : 'Could not open the print view.',
      });
    }
  }, [graph, site.name]);

  if (compact && graph) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="roadmap-compact-preview">
        {graph.nodes.slice(0, 9).map((node) => (
          <div key={node.id} className="rounded border border-border px-2 py-1.5 text-xs">
            <Badge
              variant="outline"
              className={`text-[10px] mb-1 ${recommendationStatusBadgeClass(normalizeRecommendationStatus(node.status))}`}
            >
              {recommendationStatusLabel(normalizeRecommendationStatus(node.status))}
            </Badge>
            <p className="line-clamp-2">{node.canonicalForm}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="recommendation-graph-view">
      <div className="flex items-start justify-between gap-4 flex-wrap border-b border-border-subtle pb-3">
        <div className="max-w-prose">
          <p className="text-sm text-text-secondary">Track the actions from your saved report recommendations.</p>
          {building && <p role="status" className="mt-1 text-sm text-text-muted">Updating roadmap…</p>}
          <details className="mt-1 text-sm text-text-muted" data-testid="roadmap-source-details">
            <summary className="min-h-11 cursor-pointer py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Source details</summary>
            <p>{graph ? `${graph.reportIds.length} saved report sources` : 'No roadmap extracted yet'}{entry?.lastBuiltAt ? ` · Last built ${relativeTime(Date.parse(entry.lastBuiltAt))}` : ''}</p>
            {sourceChangeCount > 0 && <p className="mt-1">{sourceChangeCount} source {sourceChangeCount === 1 ? 'change is' : 'changes are'} waiting to be included. Update the roadmap to review the revised actions.</p>}
          </details>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {graph && sourceChangeCount > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-11"
              disabled={building}
              onClick={() => void incrementalUpdate(site.id)}
              data-testid="roadmap-incremental-button"
            >
              {building ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Update roadmap ({sourceChangeCount} {sourceChangeCount === 1 ? 'change' : 'changes'})
            </Button>
          )}
          {graph && graph.nodes.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={exporting}
                data-testid="roadmap-export-trigger"
                className="inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-md border border-border bg-background px-3 text-sm font-medium transition-colors hover:bg-state-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Export
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[9rem]">
                <DropdownMenuItem className="min-h-11" onClick={() => void onExportWord()} data-testid="roadmap-export-word">
                  <FileText className="w-3.5 h-3.5 mr-2" />
                  Word (.docx)
                </DropdownMenuItem>
                <DropdownMenuItem className="min-h-11" onClick={() => void onExportPptx()} data-testid="roadmap-export-pptx">
                  <Presentation className="w-3.5 h-3.5 mr-2" />
                  PowerPoint (.pptx)
                </DropdownMenuItem>
                <DropdownMenuItem className="min-h-11" onClick={onPrint} data-testid="roadmap-export-print">
                  <Printer className="w-3.5 h-3.5 mr-2" />
                  Print
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-11"
            disabled={building}
            onClick={() => void rebuild(site.id)}
            data-testid="roadmap-rebuild-button"
          >
            {building ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Re-extract roadmap
          </Button>
        </div>
      </div>

      {allUnscheduled && (
        <div
          className="max-w-prose text-sm text-text-muted"
          data-testid="roadmap-unscheduled-banner"
        >
          These actions are not yet time-scheduled. Review the recorded timing in each action before planning the work.
        </div>
      )}

      {!graph && (
        <div
          className="max-w-prose py-6 text-sm text-text-secondary"
          data-testid="roadmap-empty-state"
        >
          Build the roadmap from your saved report recommendations using <strong>Re-extract roadmap</strong>.
        </div>
      )}

      {graph && graph.nodes.length === 0 && (
        <div className="rounded-lg border border-border p-6 text-sm text-text-secondary" data-testid="roadmap-no-nodes">
          No actionable recommendations found across site reports.
        </div>
      )}

      {graph && graph.nodes.length > 0 && (
        <RecommendationGraphFlow graph={graph} siteId={site.id} />
      )}
    </div>
  );
}
